import { PageLayoutSection } from './pageLayoutStorage';

/**
 * Service for dynamically generating tabs and layouts from page section configuration
 */

export interface DynamicTab {
  id: string;
  label: string;
  icon?: string;
  sections: PageLayoutSection[];
  order: number;
}

/**
 * Extract unique tabs from sections for a given page
 */
export const getTabsForPage = (
  sections: PageLayoutSection[],
  page: string,
  applicationId: string
): DynamicTab[] => {
  // Filter sections for this page and application
  const pageSections = sections.filter(
    s => s.application === applicationId && s.page === page && s.visible
  );

  // Group sections by tab
  const tabMap = new Map<string, PageLayoutSection[]>();

  pageSections.forEach(section => {
    const tabId = section.tab || 'overview'; // Default to 'overview' if no tab specified

    if (!tabMap.has(tabId)) {
      tabMap.set(tabId, []);
    }

    tabMap.get(tabId)!.push(section);
  });

  // Convert to array of DynamicTab objects
  const tabs: DynamicTab[] = [];
  let order = 0;

  tabMap.forEach((sections, tabId) => {
    // Sort sections by their order property
    const sortedSections = sections.sort((a, b) => a.order - b.order);

    tabs.push({
      id: tabId,
      label: formatTabLabel(tabId),
      sections: sortedSections,
      order: order++
    });
  });

  // Sort tabs alphabetically (or you could add a tab order property)
  return tabs.sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Get all sections for a specific tab
 */
export const getSectionsForTab = (
  sections: PageLayoutSection[],
  page: string,
  tab: string,
  applicationId: string
): PageLayoutSection[] => {
  return sections
    .filter(
      s =>
        s.application === applicationId &&
        s.page === page &&
        (s.tab || 'overview') === tab &&
        s.visible
    )
    .sort((a, b) => a.order - b.order);
};

/**
 * Format tab ID to display label
 */
const formatTabLabel = (tabId: string): string => {
  // Convert kebab-case or snake_case to Title Case
  return tabId
    .split(/[-_]/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Get icon name for a tab (can be customized per tab)
 */
export const getTabIcon = (tabId: string): string => {
  const iconMap: Record<string, string> = {
    'overview': 'User',
    'tasks': 'Calendar',
    'products': 'Briefcase',
    'emails': 'Mail',
    'notes': 'FileText',
    'files': 'FolderOpen',
    'history': 'Clock',
    'contacts': 'Users',
    'documents': 'FileText',
    'settings': 'Settings',
    'billing': 'DollarSign',
    'reports': 'BarChart'
  };

  return iconMap[tabId.toLowerCase()] || 'FileText';
};

/**
 * Check if a page has any configured sections
 */
export const hasConfiguredSections = (
  sections: PageLayoutSection[],
  page: string,
  applicationId: string
): boolean => {
  return sections.some(
    s => s.application === applicationId && s.page === page && s.visible
  );
};

/**
 * Get all unique pages that have sections configured
 */
export const getConfiguredPages = (
  sections: PageLayoutSection[],
  applicationId: string
): string[] => {
  const pages = new Set<string>();

  sections.forEach(section => {
    if (section.application === applicationId && section.page && section.visible) {
      pages.add(section.page);
    }
  });

  return Array.from(pages).sort();
};

export default {
  getTabsForPage,
  getSectionsForTab,
  getTabIcon,
  formatTabLabel: formatTabLabel,
  hasConfiguredSections,
  getConfiguredPages
};
